import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import Container from '../container';
import LazyImage from '../lazy-image';
import { getFluidGatsbyImage } from '../../utils/gatsby-storyblok-image-fixed';
import Arrow from '../../images/svg/arrow-icon.svg';
import parseStoryblokLink from '../../utils/parse-storyblok-link';
import { Link } from 'gatsby';
import { motion } from 'framer-motion';
import RichtextParser from '../../utils/richtext-parser';
import { styledLink } from '../../utils/styles';
import mq from '../../utils/mq';
import CtaWithArrow from '../cta-with-arrow';

const OuterLinkCardGridContainer = styled.div`
  background-color: #f8f8f8;
  padding-top: 104px;
  padding-bottom: 104px;
  ${mq.mobile(css`
    padding-top: 64px;
    padding-bottom: 64px;
  `)};
`;

const ContentGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 20px;
  padding-bottom: 80px;
  ${mq.mobile(css`
    padding-bottom: 32px;
  `)};
`;

const TitleContainer = styled.div`
  grid-column: 3 / span 8;
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  text-align: center;
  ${mq.mobile(css`
    grid-column: 1 / span 12;
  `)};
  ${mq.mobile(css`
    align-items: flex-start;
    gap: 12px;
  `)};
`;

const Title = styled.h2`
  color: #161616;
  font-size: 42px;
  font-style: normal;
  font-weight: 700;
  line-height: 115%;
  letter-spacing: -0.84px;
  text-align: center;
  max-width: 536px;
  ${mq.mobile(css`
    max-width: unset;
    text-align: left;
    font-size: 36px;
  `)};
`;

const Description = styled.div`
  color: #666;
  font-size: 18px;
  font-weight: 400;
  line-height: 160%;
  letter-spacing: -0.36px;
  text-align: center;
  text-wrap: pretty;
  max-width: 536px;
  a {
    ${styledLink};
    text-decoration: none;
  }
  ${mq.mobile(css`
    text-align: left;
  `)};
`;

const LinkCardItemsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 20px;
  width: 100%;
  @media (max-width: 1100px) {
    grid-template-columns: repeat(1, 1fr);
    grid-gap: 20px;
    width: 100%;
  }
  ${mq.mobile(css`
    grid-gap: 12px;
  `)};
`;

const LinkCardItem = styled.a<{ compactMode?: boolean }>`
  background: #fff;
  border-radius: var(--Radius-Radius-3, 16px);
  display: flex;
  padding: 26px 24px;
  align-items: center;
  gap: 24px;
  text-decoration: none;
  cursor: pointer;

  ${(props) =>
    props.compactMode &&
    css`
      align-items: center;
      justify-content: flex-start;
      gap: 24px;
    `}

  ${mq.mobile(css`
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    padding: 8px;
    gap: 0px;
  `)};
`;

const LinkCardItemImage = styled.span<{ compactMode?: boolean }>`
  width: 170px;
  aspect-ratio: 3/2;
  position: relative;
  overflow: hidden;
  will-change: transform;
  display: block;
  //background: #ececec;
  border-radius: var(--Radius-Radius-3, 16px);

  ${(props) =>
    props.compactMode &&
    css`
      width: 40px;
      height: 40px;
      border-radius: 6px;
    `};

  ${mq.mobile(css`
    aspect-ratio: 3/2;
    width: 100%;
    height: auto;
    max-height: 300px;
  `)};
`;

const LinkCardItemTextContent = styled.span<{ compactMode?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;

  ${(props) =>
    props.compactMode &&
    css`
      align-items: flex-start;
      gap: 6px;
      ${mq.mobile(css`
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
        * > span {
          display: none;
        }
      `)};
    `}
  ${mq.mobile(css`
    gap: 6px;
    padding: 16px;
    margin-top: 8px;
  `)};
`;

const LinkCardItemTitle = styled.h3<{ compactMode?: boolean }>`
  color: #161616;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 100%;
  ${(props) =>
    props.compactMode &&
    css`
      font-size: 18px;
    `}
  ${mq.mobile(css`
    font-size: 22px;
    font-style: normal;
    font-weight: 600;
    line-height: 125%;
  `)};
`;

const LinkCardItemDescription = styled.p<{ compactMode?: boolean }>`
  color: #666;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  text-wrap: pretty;
  ${mq.mobile(css`
    font-size: 16px;
    line-height: 140%;
  `)};
`;

const LinkCardItemArrow = styled.span`
  display: flex;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  aspect-ratio: 1/1;
  border-radius: 50%;
  border: 1.2px solid #d4d4d4;
  color: #666666;
  transition: all 0.15s ease-out;
  &.hovered {
    background-color: #161616;
    border: 1.2px solid #161616;
    color: #fff;
    transform: translateX(2px);
    transition: all 0.15s ease-out;
  }
  svg {
    width: 12px;
    height: 12px;
  }
  ${mq.mobile(css`
    display: none;
  `)};
`;

const SuffixText = styled.div`
  color: #959595;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 100%;
  letter-spacing: -0.15px;
  text-align: center;
  padding-top: 64px;
  a {
    color: #8a55fb;
    text-decoration-line: underline;
    text-decoration-style: solid;
  }
`;

const MobileCta = styled.div`
  display: none;
  ${mq.mobile(css`
    display: block;
    padding: 16px;
    padding-top: 8px;
  `)};
`;

export default function LinkCardGrid({
  sectionTitle,
  sectionDescription,
  linkCardItems,
  compactMode,
  suffixText
}: {
  sectionTitle: string;
  sectionDescription: string;
  linkCardItems: any[];
  compactMode?: boolean;
  suffixText?: string;
}) {
  return (
    <OuterLinkCardGridContainer>
      <Container>
        <ContentGridContainer>
          <TitleContainer>
            <Title>{sectionTitle}</Title>
            <Description
              dangerouslySetInnerHTML={{
                __html: RichtextParser(sectionDescription)
              }}
            />
          </TitleContainer>
        </ContentGridContainer>
        <LinkCardItemsContainer>
          {linkCardItems.map((item) => (
            <LinkCard key={item.id} item={item} compactMode={compactMode} />
          ))}
        </LinkCardItemsContainer>
        {suffixText && (
          <SuffixText
            dangerouslySetInnerHTML={{ __html: RichtextParser(suffixText) }}
          />
        )}
      </Container>
    </OuterLinkCardGridContainer>
  );
}

const LinkCard = ({
  item,
  compactMode
}: {
  item: any;
  compactMode?: boolean;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  console.log(item.link);
  return (
    <LinkCardItem
      key={item.id}
      href={parseStoryblokLink(item.link)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      compactMode={compactMode}
    >
      <LinkCardItemImage compactMode={compactMode}>
        <motion.div
          style={{
            willChange: 'transform',
            height: '100%',
            width: '100%'
          }}
          animate={
            isHovered
              ? {
                  scale: 1.03,
                  transition: { duration: 0.6, ease: [0.26, 1, 0.5, 1] }
                }
              : {
                  scale: 1,
                  transition: { duration: 0.3, ease: [0.26, 1, 0.5, 1] }
                }
          }
        >
          <LazyImage
            fluid={getFluidGatsbyImage(item.image)}
            width="100%"
            height="100%"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        </motion.div>
      </LinkCardItemImage>
      <LinkCardItemTextContent compactMode={compactMode}>
        <LinkCardItemTitle compactMode={compactMode}>
          {item.title}
        </LinkCardItemTitle>
        <LinkCardItemDescription compactMode={compactMode}>
          {/*compactMode && <span>- </span>*/}
          {item.description}
        </LinkCardItemDescription>
      </LinkCardItemTextContent>
      {!compactMode && (
        <LinkCardItemArrow className={isHovered ? 'hovered' : ''}>
          <Arrow />
        </LinkCardItemArrow>
      )}
      <MobileCta>
        <CtaWithArrow
          link={parseStoryblokLink(item.link)}
          label={'See more'}
          isHovered={isHovered}
          darkTheme={false}
        />
      </MobileCta>
    </LinkCardItem>
  );
};
